import { Link, useParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { getCounty, PROPERTIES, IMAGES } from "@/data";
import { LeadDialog } from "@/components/LeadForm";
import { PropertyCard } from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  ["Area overview", "An objective overview of this community within Metro Atlanta, including its general character and access to the broader region. (Placeholder content.)"],
  ["Common housing styles", "A mix of single-family homes, townhomes, and — depending on the area — condominiums and new-construction options."],
  ["General home-price categories", "Homes here span entry-level to luxury tiers. Richard can share current price ranges on request."],
  ["Parks and recreation", "Local parks, greenspace, and recreation options are available in and around this community."],
  ["Transportation & major roads", "Convenient access to major roads and highways connecting to Metro Atlanta."],
  ["Shopping, dining & attractions", "A variety of shopping, dining, and local attractions serve the area."],
  ["New-construction information", "Select new-construction communities may be available. Contact Richard for current options."],
];

export default function City() {
  const { slug, city } = useParams();
  const county = getCounty(slug);
  const cityName = (county?.cities || []).find((c) => c.toLowerCase().replace(/\s/g, "-") === city) || (city || "").replace(/-/g, " ");
  const listings = PROPERTIES.filter((p) => p.county === slug).slice(0, 3);

  return (
    <div className="bg-cream pt-24">
      <section className="px-6 md:px-12 py-14 max-w-7xl mx-auto">
        <Reveal><Link to={`/areas/${slug}`} className="font-mono text-xs uppercase tracking-[0.2em] text-green hover:text-gold">{county?.name || "County"} →</Link></Reveal>
        <Reveal delay={0.1}><h1 className="font-serif text-5xl md:text-6xl text-navy tracking-tighter mt-3 mb-2 capitalize flex items-center gap-3"><MapPin className="text-gold" size={32} />{cityName}</h1></Reveal>
        <Reveal delay={0.15}><p className="text-charcoal/60 max-w-xl">A community profile in {county?.name || "Metro Atlanta"}. All descriptions are objective and placeholder — no subjective neighborhood claims.</p></Reveal>
      </section>

      <section className="px-6 md:px-12 pb-8 max-w-7xl mx-auto grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          {SECTIONS.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.04}>
              <h2 className="font-serif text-2xl text-navy mb-2">{t}</h2>
              <p className="text-charcoal/75 leading-relaxed">{d}</p>
            </Reveal>
          ))}
          <Reveal>
            <h2 className="font-serif text-2xl text-navy mb-2">Map</h2>
            <div className="rounded-2xl overflow-hidden border border-black/10 h-56 bg-navy/5 flex items-center justify-center text-charcoal/40 font-mono text-sm" data-testid="map-placeholder">
              Map placeholder — Google Maps can be connected later
            </div>
          </Reveal>
        </div>
        <aside>
          <div className="bg-white rounded-3xl p-7 border border-black/5 sticky top-28">
            <img src={IMAGES.interior2} alt="" className="rounded-xl h-32 w-full object-cover mb-5" />
            <h3 className="font-serif text-xl text-navy mb-4">Interested in {cityName}?</h3>
            <div className="flex flex-col gap-3">
              <LeadDialog type="buyer" source={`city-${city}-list`} title="Request a Personalized Property List" defaults={{ county: county?.name, city: cityName }}
                trigger={<Button data-testid="city-list-cta" className="bg-gold text-navy font-semibold rounded-full w-full">Request a property list</Button>} />
              <LeadDialog type="consultation" source={`city-${city}-consult`} title="Schedule a Consultation" defaults={{ county: county?.name, city: cityName }}
                trigger={<Button data-testid="city-consult-cta" className="bg-navy text-cream font-semibold rounded-full w-full">Schedule a consultation</Button>} />
            </div>
          </div>
        </aside>
      </section>

      <section className="px-6 md:px-12 py-16 max-w-7xl mx-auto">
        <h2 className="font-serif text-3xl text-navy mb-2">Featured listings</h2>
        <p className="text-charcoal/50 text-sm mb-8">Sample placeholder listings — not live MLS data.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((p) => <PropertyCard key={p.id} p={p} />)}
        </div>
      </section>
    </div>
  );
}
